/**
 * CORE-1384 — выбор уменьшенного варианта картинки под размер карточки.
 *
 * Главная качала оригиналы: три снимка на 7,9 МБ, чтобы показать их 132 × 161 и
 * 323 × 286. Бэкенд теперь отдаёт рядом с `coverUrl` / `avatarUrl` набор
 * вариантов «ширина → ссылка», и задача этого модуля — выбрать из них один.
 *
 * Почему не `srcset`. Карточки рисуются `<Image>` из react-native, а он берёт
 * ровно один `uri`: браузерного механизма здесь нет ни на нативе, ни на вебе
 * (react-native-web рендерит фон, а не `<img>`). Поэтому выбор делается явно —
 * это и есть «эквивалент используемого UI-стека», которого просит задача.
 *
 * Правило намеренно НИКОГДА не возвращается к оригиналу, когда варианты есть.
 * Даже самый большой вариант меньше нужного — это лёгкая мягкость на экране с
 * тройной плотностью; оригинал в том же месте — четыре мегабайта. Оригинал
 * остаётся ровно для одного случая: превью нет вовсе.
 *
 * Возврат QA 03.10 показал, что «нет набора» и «нет превью» — разные вещи, а
 * здесь они совпадали. Резервная выдача мастеров отдаёт одну ссылку на превью
 * без набора; правило читало это как «вариантов нет» и брало оригинал — 68 КБ
 * вместо 7 КБ при показе 148 × 131. Отсюда `previewUrl`: превью, ширина
 * которого неизвестна, но которое точно существует. Ширину из имени файла не
 * выводим — это была бы догадка о чужом соглашении об именах.
 */

/** То, что приходит в `coverVariants` / `avatarVariants`: ширина → ссылка. */
export type ImageVariants = Record<string, string> | null | undefined;

export interface PickVariantInput {
    /** Набор из выдачи. */
    variants: ImageVariants;
    /**
     * Единственное известное превью, когда набора нет (старая выдача). Ширина
     * неизвестна, поэтому выбирать не из чего — но это всё равно превью, и оно
     * лучше оригинала при любом размере показа.
     */
    previewUrl?: string | null;
    /** Оригинал — им отвечаем, только если нет ни набора, ни превью. */
    originalUrl: string | null | undefined;
    /** Ширина, которой картинка показывается, в точках. */
    displayWidth: number;
    /** Плотность экрана. Меньше единицы не бывает; мусор считаем единицей. */
    pixelRatio: number;
}

/**
 * Ссылка, которую и надо грузить.
 *
 * `null` — грузить нечего: нет ни вариантов, ни оригинала.
 */
export function pickImageVariant({
    variants,
    previewUrl,
    originalUrl,
    displayWidth,
    pixelRatio,
}: PickVariantInput): string | null {
    const sizes = parseVariants(variants);
    if (sizes.length === 0) return previewUrl || originalUrl || null;

    const dpr = Number.isFinite(pixelRatio) && pixelRatio > 0 ? pixelRatio : 1;
    const width = Number.isFinite(displayWidth) && displayWidth > 0 ? displayWidth : 0;
    const need = Math.ceil(width * dpr);

    // Первый вариант, которого хватает. Список отсортирован по возрастанию, так
    // что это и есть самый дешёвый из подходящих.
    const enough = sizes.find(s => s.width >= need);
    if (enough) return enough.url;

    // Не хватило ни одного — берём самый большой. См. заголовок: мягкость
    // дешевле мегабайтов, а оригинал здесь и есть тот самый дефект.
    return sizes[sizes.length - 1].url;
}

/**
 * Набор в виде отсортированного списка. Мусорный ключ пропускается, а не роняет
 * остальное: выдача может пополниться размерами раньше, чем клиент об этом
 * узнает, и один неожиданный ключ не повод возвращаться к оригиналу.
 */
function parseVariants(variants: ImageVariants): { width: number; url: string }[] {
    if (!variants || typeof variants !== 'object') return [];
    const out: { width: number; url: string }[] = [];
    for (const [key, url] of Object.entries(variants)) {
        const width = Number(key);
        if (!Number.isFinite(width) || width <= 0) continue;
        if (typeof url !== 'string' || url.length === 0) continue;
        out.push({ width, url });
    }
    return out.sort((a, b) => a.width - b.width);
}
